import type { Locale } from "@tutor/protocol";
import { z } from "zod";

// Calls to the API from the sign-in and set-up screens. The app and the API share one
// origin (ADR-0022), so parent cookies are first-party. Responses are validated; any
// failure becomes a typed ApiError rather than an exception.

export type ApiError =
  | { readonly kind: "offline" }
  | { readonly kind: "rate_limited" }
  /** `code` is the API's `error` field, or Better Auth's `code` (e.g. INVALID_OTP). */
  | { readonly kind: "http"; readonly status: number; readonly code: string | undefined };

export type Result<T> =
  { readonly ok: true; readonly value: T } | { readonly ok: false; readonly error: ApiError };

const errorBody = z.looseObject({ error: z.string().optional(), code: z.string().optional() });
const locale = z.enum(["en", "fr"]) satisfies z.ZodType<Locale>;

async function request<T>(
  path: string,
  init: { method?: string; body?: unknown; device?: string },
  schema: z.ZodType<T>,
): Promise<Result<T>> {
  const headers: Record<string, string> = { accept: "application/json" };
  if (init.body !== undefined) headers["content-type"] = "application/json";
  if (init.device !== undefined) headers["authorization"] = `Device ${init.device}`;
  let res: Response;
  try {
    res = await fetch(path, {
      method: init.method ?? "GET",
      headers,
      credentials: "include",
      ...(init.body === undefined ? {} : { body: JSON.stringify(init.body) }),
    });
  } catch {
    return { ok: false, error: { kind: "offline" } };
  }
  const json: unknown = res.status === 204 ? null : await res.json().catch(() => null);
  if (res.status === 429) return { ok: false, error: { kind: "rate_limited" } };
  if (!res.ok) {
    const body = errorBody.safeParse(json);
    const code = body.success ? (body.data.error ?? body.data.code) : undefined;
    return { ok: false, error: { kind: "http", status: res.status, code } };
  }
  const parsed = schema.safeParse(json);
  if (!parsed.success) {
    return { ok: false, error: { kind: "http", status: res.status, code: "invalid_response" } };
  }
  return { ok: true, value: parsed.data };
}

const anything = z.unknown();

// Parent sign-in (Better Auth email OTP under /api/auth, ADR-0018).

export function sendCode(email: string) {
  return request(
    "/api/auth/email-otp/send-verification-otp",
    { method: "POST", body: { email, type: "sign-in" } },
    anything,
  );
}

export function signInWithCode(email: string, otp: string) {
  return request("/api/auth/sign-in/email-otp", { method: "POST", body: { email, otp } }, anything);
}

/** Ends the parent session on this device (cancelled set-up). */
export function signOut() {
  return request("/api/auth/sign-out", { method: "POST", body: {} }, anything);
}

// Parent API.

const parentProfile = z.looseObject({ id: z.string(), displayName: z.string(), locale });
export type ParentProfile = z.infer<typeof parentProfile>;

const me = z.looseObject({
  profiles: z.array(parentProfile),
  /** Invite-only alpha; absent when the API has no invite step. */
  alpha: z.looseObject({ invited: z.boolean() }).optional(),
});
export type Me = z.infer<typeof me>;

export function getMe() {
  return request("/v1/me", {}, me);
}

export function redeemInvite(code: string) {
  return request("/v1/invites/redeem", { method: "POST", body: { code } }, anything);
}

export function getPrivacyNotice() {
  return request("/v1/privacy-notice", {}, z.looseObject({ version: z.string() }));
}

export function createProfile(input: {
  displayName: string;
  birthYear: number;
  locale: Locale;
  noticeVersion: string;
}) {
  return request(
    "/v1/profiles",
    {
      method: "POST",
      body: {
        displayName: input.displayName,
        birthYear: input.birthYear,
        locale: input.locale,
        consent: { noticeVersion: input.noticeVersion },
      },
    },
    parentProfile,
  );
}

export function setPin(profileId: string, pin: string) {
  return request(
    `/v1/profiles/${encodeURIComponent(profileId)}/pin`,
    { method: "PUT", body: { pin } },
    anything,
  );
}

const enrolled = z.looseObject({ deviceId: z.string(), credential: z.string() });
export type EnrolledDevice = z.infer<typeof enrolled>;

/** Enrolls this device. The API ends the parent session in the same response. */
export function enrollDevice(label: string, profileIds: readonly string[]) {
  return request("/v1/devices", { method: "POST", body: { label, profileIds } }, enrolled);
}

// Child's device (ADR-0019).

export function redeemPairingCode(code: string, label: string) {
  return request("/v1/devices/redeem", { method: "POST", body: { code, label } }, enrolled);
}

const deviceProfile = z.looseObject({
  profileId: z.string(),
  displayName: z.string(),
  locale,
  pinSet: z.boolean(),
});
export type DeviceProfile = z.infer<typeof deviceProfile>;

export function getDeviceProfiles(credential: string) {
  return request(
    "/v1/device/profiles",
    { device: credential },
    z.looseObject({ profiles: z.array(deviceProfile) }),
  );
}

const learnerSession = z.looseObject({
  token: z.string(),
  expiresAt: z.union([z.string(), z.number()]),
  locale: locale.optional(),
  /** The household may use voice mode (decided by the API); absent from older APIs. */
  voiceEntitled: z.boolean().optional(),
});

export function startLearnerSession(credential: string, profileId: string, pin: string) {
  return request(
    "/v1/learner-sessions",
    { method: "POST", body: { profileId, pin }, device: credential },
    learnerSession,
  );
}
